import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  LayoutPanelLeft,
  Layers,
  LayoutGrid,
  Rocket,
  Settings,
  CreditCard,
  BarChart3,
  User,
  Users,
  Plus,
  Palette,
  Archive,
  Globe,
  Shield,
  DollarSign,
  UserPlus,
  LucideIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { NavigationSection, NavigationItem } from '@/types/sidebar';
import { useSidebar } from './SidebarProvider';
import { useAppCache } from '@/contexts/AppCacheContext';

/**
 * Navigation Menu — Swiss-style sidebar
 *
 * Expanded state: labels only (no icons), grouped by `section` field
 *   with caption-style section headers.
 * Collapsed state: icons only, 36px square targets, HTML title tooltip.
 *
 * Active state uses a plain `bg-surface` fill — no colored bars,
 * no colored text, no underlines.
 */

// Icon mapping for string-based icon names
const iconMap: Record<string, LucideIcon> = {
  LayoutDashboard,
  LayoutPanelLeft,
  Layers,
  LayoutGrid,
  Rocket,
  Settings,
  CreditCard,
  BarChart3,
  User,
  Users,
  Plus,
  Palette,
  Archive,
  Globe,
  Shield,
  DollarSign,
  UserPlus
};

interface NavigationMenuProps {
  sections: NavigationSection[];
  className?: string;
  /** When true, force expanded layout (labels + section headers) regardless of viewport. */
  forceExpanded?: boolean;
}

interface NavigationItemProps {
  item: NavigationItem;
  isActive: boolean;
  isCollapsed: boolean;
  isMobile: boolean;
}

// Individual navigation item component
const NavigationItemComponent: React.FC<NavigationItemProps> = ({
  item,
  isActive,
  isCollapsed,
  isMobile
}) => {
  const { close } = useSidebar();

  const handleClick = () => {
    if (item.disabled) return;
    if (isMobile) close();
  };

  const IconComponent =
    typeof item.icon === 'string' ? iconMap[item.icon] : item.icon;

  const baseClasses = cn(
    'flex items-center text-foreground cursor-pointer',
    isCollapsed
      ? cn(
          // Square hitbox centered in the bar — no full-width hover rectangle.
          'w-10 h-10 mx-auto justify-center rounded-full no-underline',
          !isActive && 'hover:bg-foreground/5',
          isActive && 'bg-foreground text-background'
        )
      : cn(
          // Hitbox hugs the text only — no full-row rectangle. Hover fills
          // a pill behind the label. Pill height (32px line + 8px padding)
          // intentionally matches the 40px collapsed icon square so the
          // active state reads the same at either breakpoint.
          'w-fit self-start ml-3 mr-5 px-2 py-1 rounded text-2xl font-medium no-underline leading-8',
          !isActive && 'hover:bg-foreground/5',
          isActive && 'bg-foreground text-background'
        )
  );

  const renderContent = () => {
    if (isCollapsed) {
      return IconComponent ? (
        <IconComponent className="h-5 w-5" strokeWidth={2} />
      ) : null;
    }
    return (
      <span className="flex-1 text-left truncate">{item.label}</span>
    );
  };

  const commonProps = {
    'aria-label': isCollapsed ? item.label : undefined,
    title: isCollapsed ? item.label : undefined,
    'aria-current': isActive ? ('page' as const) : undefined
  };

  if (item.href) {
    if (item.external) {
      return (
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className={baseClasses}
          {...commonProps}
        >
          {renderContent()}
        </a>
      );
    }

    return (
      <Link
        to={item.href}
        onClick={handleClick}
        className={baseClasses}
        {...commonProps}
      >
        {renderContent()}
      </Link>
    );
  }

  if (item.action) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={baseClasses}
        {...commonProps}
      >
        {renderContent()}
      </button>
    );
  }

  return (
    <div className={cn(baseClasses, 'cursor-default')} {...commonProps}>
      {renderContent()}
    </div>
  );
};

interface SectionGroup {
  key: string;
  items: NavigationItem[];
}

// Build section groups from flat items, preserving first-seen order
const groupItemsBySection = (items: NavigationItem[]): SectionGroup[] => {
  const order: string[] = [];
  const buckets = new Map<string, NavigationItem[]>();

  for (const item of items) {
    const key = item.section ?? '';
    if (!buckets.has(key)) {
      buckets.set(key, []);
      order.push(key);
    }
    buckets.get(key)!.push(item);
  }

  return order.map((key) => ({ key, items: buckets.get(key)! }));
};

// Main navigation menu component
export const NavigationMenu: React.FC<NavigationMenuProps> = ({
  sections,
  className,
  forceExpanded = false
}) => {
  const location = useLocation();
  const { hasPermission, permissionsLoading } = useAppCache();
  const { isCollapsed: sidebarCollapsed, isMobile } = useSidebar();

  // Expanded when the caller explicitly requests it (mobile drawer uses
  // forceExpanded). Otherwise follow the sidebar's collapsed state — the
  // fixed desktop sidebar is visible at all breakpoints and must render
  // icons whenever the rail is narrow (tablet/mobile), not just tablet.
  const isCollapsed = !forceExpanded && sidebarCollapsed;

  // Flatten all incoming sections into a single item list, then regroup by `section` field
  const visibleItems = useMemo(() => {
    const flat = sections.flatMap((s) => s.items);

    // While permissions are loading, show only ungated items. Gated items
    // appear once hasPermission confirms — never fail open.
    if (permissionsLoading) {
      return flat.filter((item) => !item.permission);
    }

    return flat.filter((item) => {
      if (!item.permission) return true;
      return hasPermission(item.permission);
    });
  }, [sections, hasPermission, permissionsLoading]);

  const groups = useMemo(() => groupItemsBySection(visibleItems), [visibleItems]);

  if (visibleItems.length === 0) return null;

  return (
    <div className={cn('flex flex-col pt-8 pb-4', className)}>
      {groups.map((group, groupIndex) => {
        const isActiveItem = (item: NavigationItem) => {
          if (!item.href) return false;
          const { pathname } = location;
          if (pathname === item.href) return true;
          // Child routes activate the parent: /sites/abc activates /sites,
          // /domains/new activates /domains, etc.
          if (item.href !== '/' && pathname.startsWith(item.href + '/')) return true;
          // Root path defaults to /sites.
          if (pathname === '/' && item.href === '/sites') return true;
          return false;
        };

        return (
          <div
            key={group.key || `group-${groupIndex}`}
            className={groupIndex === 0 ? undefined : 'mt-subsection'}
          >
            {!isCollapsed && group.key && (
              <div className="text-xs capitalize text-muted-foreground px-5 mb-1">
                {group.key}
              </div>
            )}
            <nav
              className={cn(
                'flex flex-col gap-1',
                isCollapsed && 'items-center'
              )}
              role="navigation"
              aria-label={group.key || 'Navigation'}
            >
              {group.items.map((item, index) => (
                <NavigationItemComponent
                  key={`${group.key}-${index}`}
                  item={item}
                  isActive={isActiveItem(item)}
                  isCollapsed={isCollapsed}
                  isMobile={isMobile}
                />
              ))}
            </nav>
          </div>
        );
      })}
    </div>
  );
};

export default NavigationMenu;
